import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  FlaskConical,
  AlertTriangle,
  CheckCircle2,
  Calculator,
  Thermometer,
  ShieldCheck,
  Syringe,
  ArrowRight,
} from "lucide-react";
import {
  SuppliesIllustration,
  StepCleanIllustration,
  StepDrawIllustration,
  StepInjectIllustration,
  StepSwirlIllustration,
  StepLabelIllustration,
  StepStoreIllustration,
  SyringeMeasurementIllustration,
} from "@/components/ReconstitutionIllustrations";
import { PageNavigation, PageBreadcrumb } from "@/components/PageNavigation";

export const ReconstitutionPage = () => {
  const { t } = useTranslation();

  // Requirements list
  const requirementsList = [
    t('reconstitution.requirementsList.peptideVial'),
    t('reconstitution.requirementsList.reconSolution'),
    t('reconstitution.requirementsList.insulinSyringe'),
    t('reconstitution.requirementsList.syringe3ml'),
    t('reconstitution.requirementsList.alcoholPads'),
    t('reconstitution.requirementsList.sharpie'),
  ];

  // Core rules
  const coreRules = [
    { number: 1, text: t('reconstitution.rules.rule1') },
    { number: 2, text: t('reconstitution.rules.rule2') },
    { number: 3, text: t('reconstitution.rules.rule3') },
    { number: 4, text: t('reconstitution.rules.rule4') },
    { number: 5, text: t('reconstitution.rules.rule5'), isWarning: true },
  ];

  // Reconstitution steps
  const reconSteps = [
    { number: 1, title: t('reconstitution.step1Title'), description: t('reconstitution.step1Desc'), Illustration: StepCleanIllustration },
    { number: 2, title: t('reconstitution.step2Title'), description: t('reconstitution.step2Desc'), Illustration: StepDrawIllustration },
    { number: 3, title: t('reconstitution.step3Title'), description: t('reconstitution.step3Desc'), Illustration: StepInjectIllustration },
    { number: 4, title: t('reconstitution.step4Title'), description: t('reconstitution.step4Desc'), Illustration: StepSwirlIllustration },
    { number: 5, title: t('reconstitution.step5Title'), description: t('reconstitution.step5Desc'), Illustration: StepLabelIllustration },
    { number: 6, title: t('reconstitution.step6Title'), description: t('reconstitution.step6Desc'), Illustration: StepStoreIllustration },
  ];

  // Drawing tips
  const drawingTips = [
    t('reconstitution.drawingTips.tip1'),
    t('reconstitution.drawingTips.tip2'),
    t('reconstitution.drawingTips.tip3'),
    t('reconstitution.drawingTips.tip4'),
  ];

  // Administration tips
  const adminTips = [
    { text: t('reconstitution.adminTips.tip1') },
    { text: t('reconstitution.adminTips.tip2'), highlight: true },
    { text: t('reconstitution.adminTips.tip3') },
    { text: t('reconstitution.adminTips.tip4') },
    { text: t('reconstitution.adminTips.tip5') },
    { text: t('reconstitution.adminTips.tip6') },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Top Breadcrumb Navigation */}
      <PageBreadcrumb currentPath="/reconstitution" />

      {/* Page Header */}
      <div className="w-full py-8 px-4 border-b border-border/50 bg-gradient-to-b from-muted/30 to-background">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2.5 bg-primary rounded-lg">
              <FlaskConical className="w-6 h-6 text-primary-foreground" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground">
              {t('reconstitution.title')}
            </h1>
          </div>
          <p className="text-lg text-primary font-medium mb-1">{t('reconstitution.quickGuide')}</p>
          <p className="text-muted-foreground">
            {t('reconstitution.subtitle')}
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8 space-y-10">
        
        {/* Requirements Section */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <CheckCircle2 className="w-5 h-5 text-primary" />
            <h2 className="text-xl font-bold text-foreground">{t('reconstitution.requirements')}</h2>
          </div>
          <Card className="overflow-hidden">
            <CardContent className="p-0">
              <div className="grid md:grid-cols-2 gap-0">
                {/* Requirements list */}
                <div className="p-6 space-y-2">
                  <ul className="space-y-2">
                    {requirementsList.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-foreground">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                {/* Illustration */}
                <div className="bg-muted/20 p-4 flex items-center justify-center border-t md:border-t-0 md:border-l border-border/50">
                  <SuppliesIllustration className="w-full max-w-md h-auto" />
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Core Rules Section */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck className="w-5 h-5 text-primary" />
            <h2 className="text-xl font-bold text-foreground">{t('reconstitution.coreRules')}</h2>
          </div>
          <Card>
            <CardContent className="p-6">
              <div className="space-y-3">
                {coreRules.map((rule) => (
                  <div
                    key={rule.number}
                    className={`flex items-start gap-3 p-3 rounded-lg ${
                      rule.isWarning 
                        ? "bg-amber-50 border border-amber-200" 
                        : "bg-muted/30"
                    }`}
                  >
                    <Badge 
                      variant="outline" 
                      className={`flex-shrink-0 ${
                        rule.isWarning 
                          ? "bg-amber-100 text-amber-700 border-amber-300" 
                          : "bg-primary/10 text-primary border-primary/30"
                      }`}
                    >
                      {t('reconstitution.rule')} {rule.number}
                    </Badge>
                    <p className={`text-sm ${rule.isWarning ? "text-amber-800 font-medium" : "text-foreground"}`}>
                      {rule.text}
                    </p>
                    {rule.isWarning && (
                      <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Reconstitution Steps */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <Syringe className="w-5 h-5 text-primary" />
            <h2 className="text-xl font-bold text-foreground">{t('reconstitution.steps')}</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {reconSteps.map((step) => {
              const Illustration = step.Illustration;
              return (
                <Card key={step.number} className="overflow-hidden hover:shadow-md transition-shadow">
                  <CardContent className="p-0">
                    <div className="bg-muted/20 p-4 flex items-center justify-center h-44 border-b border-border/30">
                      <Illustration className="w-32 h-36" />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-sm font-bold flex items-center justify-center">
                          {step.number}
                        </span>
                        <h3 className="font-semibold text-foreground">{step.title}</h3>
                      </div>
                      <p className="text-sm text-muted-foreground">{step.description}</p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>

        <Separator />

        {/* Understanding Measurements */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <Calculator className="w-5 h-5 text-primary" />
            <h2 className="text-xl font-bold text-foreground">{t('reconstitution.measurements')}</h2>
          </div>
          <Card>
            <CardContent className="p-6">
              <div className="bg-muted/20 rounded-lg p-4 mb-6 overflow-x-auto">
                <SyringeMeasurementIllustration className="w-full min-w-[360px] h-auto" />
              </div>
              <div className="grid sm:grid-cols-3 gap-4 text-center">
                <div className="p-4 bg-primary/5 rounded-lg border border-primary/10">
                  <p className="text-2xl font-bold text-primary">100 {t('reconstitution.units')}</p>
                  <p className="text-sm text-muted-foreground">= 1.0 mL</p>
                </div>
                <div className="p-4 bg-primary/5 rounded-lg border border-primary/10">
                  <p className="text-2xl font-bold text-primary">10 {t('reconstitution.units')}</p>
                  <p className="text-sm text-muted-foreground">= 0.1 mL</p>
                </div>
                <div className="p-4 bg-primary/5 rounded-lg border border-primary/10">
                  <p className="text-2xl font-bold text-primary">1 {t('reconstitution.units').slice(0, -1)}</p>
                  <p className="text-sm text-muted-foreground">= 0.01 mL</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Formulas and Example */}
        <section className="grid md:grid-cols-2 gap-6">
          {/* Formulas */}
          <Card>
            <CardContent className="p-6">
              <h3 className="text-lg font-bold text-foreground mb-4">{t('reconstitution.formulas')}</h3>
              <div className="space-y-4">
                <div className="p-3 bg-muted/30 rounded-lg">
                  <p className="font-mono text-sm font-semibold text-foreground">
                    mg/mL = total mg ÷ mL added
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">{t('reconstitution.concentrationFormula')}</p>
                </div>
                <div className="p-3 bg-muted/30 rounded-lg">
                  <p className="font-mono text-sm font-semibold text-foreground">
                    mg per unit = (mg/mL) ÷ 100
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">{t('reconstitution.dosePerUnitFormula')}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Example */}
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="p-6">
              <h3 className="text-lg font-bold text-foreground mb-4">{t('reconstitution.example')}</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-medium text-foreground">10 mg + 2 mL</span>
                  <ArrowRight className="w-4 h-4 text-muted-foreground" />
                  <span className="font-semibold text-primary">5 mg/mL</span>
                </div>
                <div className="text-sm text-muted-foreground pl-4 border-l-2 border-primary/30 space-y-1">
                  <p>10 units = <span className="font-medium text-foreground">0.5 mg</span></p>
                  <p>20 units = <span className="font-medium text-foreground">1.0 mg</span></p>
                </div>
              </div>
              <Separator className="my-4" />
              <p className="text-sm text-muted-foreground">
                {t('reconstitution.calculatorRef')}{" "}
                <Link to="/calculator" className="text-primary font-medium hover:underline">
                  {t('reconstitution.dosageCalculator')}
                </Link>{" "}
                {t('reconstitution.forMoreDetails')}
              </p>
            </CardContent>
          </Card>
        </section>

        {/* Drawing Tips */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <Syringe className="w-5 h-5 text-primary" />
            <h2 className="text-xl font-bold text-foreground">{t('reconstitution.drawing')}</h2>
          </div>
          <Card>
            <CardContent className="p-6">
              <ul className="space-y-2">
                {drawingTips.map((tip, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-foreground">{tip}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </section>

        {/* Administration */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <Thermometer className="w-5 h-5 text-primary" />
            <h2 className="text-xl font-bold text-foreground">{t('reconstitution.administration')}</h2>
          </div>
          <Card>
            <CardContent className="p-6">
              <ul className="space-y-2 mb-4">
                {adminTips.map((tip, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                    <span className={tip.highlight ? "text-foreground font-medium" : "text-foreground"}>
                      {tip.text}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="text-sm text-muted-foreground italic">
                {t('reconstitution.adminNote')}
              </p>
            </CardContent>
          </Card>
        </section>

        {/* Final Reminder */}
        <section>
          <Card className="bg-primary text-primary-foreground overflow-hidden">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <AlertTriangle className="w-8 h-8 flex-shrink-0 opacity-90" />
                <div>
                  <h3 className="text-lg font-bold mb-2">{t('reconstitution.finalReminder')}</h3>
                  <p className="text-primary-foreground/90 font-medium">
                    {t('reconstitution.eachPeptideDifferent')}
                  </p>
                  <p className="text-primary-foreground/90 font-medium">
                    {t('reconstitution.neverAssumeDosing')}
                  </p>
                  <Link to="/calculator">
                    <Button variant="secondary" size="sm" className="mt-4">
                      <Calculator className="w-4 h-4 mr-2" />
                      {t('reconstitution.goToCalculator')}
                    </Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

      </div>

      {/* Page Navigation */}
      <PageNavigation currentPath="/reconstitution" />

      {/* Disclaimer Footer */}
      <div className="border-t border-border/50 bg-muted/20 py-6 px-4">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs text-muted-foreground text-center leading-relaxed">
            <strong>{t('common.disclaimer')}</strong> {t('common.disclaimerText')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ReconstitutionPage;
